import React, { useEffect, useRef, useState } from "react";
import { Typography, Box, Tooltip } from "@mui/material";
import "./ChatPopup.css"; // Import your CSS file for styling
import CloseIcon from "@mui/icons-material/Close";
import ChatBot from "../ChatBot/ChatBot";
import { useTranslation } from "react-i18next";

export default function ChatPopup() {
	const [chatVisible, setChatVisible] = useState(false);
	const [hoverVisible, setHoverVisible] = useState(false);
	const [popupSize, setPopupSize] = useState({ width: 460, height: 760 });
	const resizeRef = useRef(null);
	const NAVBAR_HEIGHT = 160; // px reserved above the panel (two navbar rows + buffer)
	const ICON_SRC = "/chip-icon.png";
	const {t} = useTranslation();

	const clampHeight = (h) => Math.min(Math.max(460, h), window.innerHeight - NAVBAR_HEIGHT - 25);
	const clampWidth = (w) => Math.min(Math.max(360, w), Math.floor(window.innerWidth * 0.9));

	useEffect(() => {
		try {
			const raw = localStorage.getItem("chip_chat_size");
			if (raw) {
				const parsed = JSON.parse(raw);
				if (Number.isFinite(parsed?.width) && Number.isFinite(parsed?.height)) {
					setPopupSize({
						width: clampWidth(parsed.width),
						height: clampHeight(parsed.height),
					});
				}
			}
		} catch (_error) {}

		const handleWindowResize = () => {
			setPopupSize((prev) => ({
				width: clampWidth(prev.width),
				height: clampHeight(prev.height),
			}));
		};
		window.addEventListener("resize", handleWindowResize);

		const showTimer = setTimeout(() => setHoverVisible(true), 1000);
		const hideTimer = setTimeout(() => setHoverVisible(false), 5000);

		return () => {
			window.removeEventListener("resize", handleWindowResize);
			clearTimeout(showTimer);
			clearTimeout(hideTimer);
		};
	}, []);

	const handleChatOpen = () => {
		const newVisible = !chatVisible;
		setChatVisible(newVisible);
	};

	useEffect(() => {
		if (chatVisible) {
			setHoverVisible(false);
		}
	}, [chatVisible]);

	const startResizeForEdge = (event, edge) => {
		event.preventDefault();
		resizeRef.current = {
			edge,
			startX: event.clientX,
			startY: event.clientY,
			startWidth: popupSize.width,
			startHeight: popupSize.height,
		};
		document.body.style.userSelect = "none";
	};

	useEffect(() => {
		const onMove = (event) => {
			if (!resizeRef.current) return;
			const { startX, startY, startWidth, startHeight, edge } = resizeRef.current;
			const dx = startX - event.clientX;
			const dy = startY - event.clientY;

			let width = startWidth;
			let height = startHeight;
			if (edge === "left") {
				width = clampWidth(startWidth + dx);
			}
			if (edge === "top") {
				height = clampHeight(startHeight + dy);
			}
			setPopupSize({ width, height });
		};

		const onUp = () => {
			if (!resizeRef.current) return;
			resizeRef.current = null;
			document.body.style.userSelect = "";
			try {
				localStorage.setItem("chip_chat_size", JSON.stringify(popupSize));
			} catch (_error) {}
		};

		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
		return () => {
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
		};
	}, [popupSize]);

	return (
		<div>
			<div
				className={`assistant-icon ${chatVisible ? "assistant-icon-open" : ""}`}
				onClick={handleChatOpen}
				onMouseEnter={() => {
					chatVisible ? setHoverVisible(false) : setHoverVisible(true);
				}}
				onMouseLeave={() => setHoverVisible(false)}>
				{chatVisible ? (
					<CloseIcon />
				) : (
					<img src={ICON_SRC} alt="CHIP" className="assistant-icon-image" />
				)}
			</div>
			<div>
				<Box className={`chat-hover ${hoverVisible ? "show" : ""}`}>
					<div style={{ padding: "10px", textAlign: "center" }}>
						<Typography variant="body1">
							{t('UI_TEXT.CHAT_WELCOME_PREFIX')} <b>{t('UI_TEXT.CHAT_WELCOME_HIGHLIGHT')}</b>
						</Typography>
					</div>
					
					<div className="chat-arrow" />
				</Box>
				
			
				
				
				<Box
					className={`chat-popup ${chatVisible ? "show" : ""}`}
					sx={{ borderBottom: "3px solid", borderColor: "primary.main", width: `${popupSize.width}px`, height: `${popupSize.height}px` }}>
					<Tooltip title= {t('TITLES.CLOSE_CHAT')} placement="top" arrow>
						<Box
							sx={{
								position: "absolute",
								right: -10,
								top: -10,
								padding: "4px",
								borderRadius: "50%",
								backgroundColor: "white",
								color: "error.main",
								zIndex: 1051,
								boxShadow: 2,
								height: "25px",
								width: "25px",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								"&:hover": {
									cursor: "pointer",
									backgroundColor: "error.main",
									color: "white",
								},
							}}
							onClick={handleChatOpen}>
							<CloseIcon fontSize="18px" />
						</Box>
					</Tooltip>
					<Box sx={{ flex: 1, width: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
						<ChatBot />

					</Box>
					<Box className="chat-resize-handle-left" onMouseDown={(e) => startResizeForEdge(e, "left")} />
					<Box className="chat-resize-handle-top" onMouseDown={(e) => startResizeForEdge(e, "top")} />
							
					<div className="chat-arrow" />
				</Box>
			</div>
		</div>
	);
}
